import '../create-or-update-tenant.scss'

import { Col, notification, Result, Row, Skeleton } from 'antd'
import axios from 'axios'
import { Formik, FormikHelpers } from 'formik'
import { Form, FormItem, Input, InputNumber, Select, SubmitButton, Switch } from 'formik-antd'
import React, { useEffect, useState } from 'react'
import { Column } from 'react-display-flex'
import useSWR from 'swr'

import { fetchByDomain, fetchDetail } from '../../../api'
import { EditableSelect } from '../../../components'
import { filterProps, parseApiErrorResponse } from '../../../components/forms/utils'
import { localeNames } from '../../../date/date-properties'
import { currencies } from '../codes'
import { messages } from '../messages'
import { TenantFormValues, updateTenantInitialData } from '../models'
import { validationSchema } from './validation'

interface Props {
  tenantId: number
  onSuccess: () => void
  segmentCodes: string[]
}

export const UpdateTenant = ({ tenantId, onSuccess, segmentCodes }: Props) => {
  const [initialValues, setInitialValues] = useState<TenantFormValues>(updateTenantInitialData)

  const tenantDetailUrl = `/tenants/${tenantId}`

  const { data: tenant } = useSWR(tenantId ? tenantDetailUrl : null, fetchDetail)
  const { data: issuers, error } = useSWR(tenant ? ['/issuers', tenant.domain] : null, fetchByDomain)

  const onSubmit = async (
    values: TenantFormValues,
    { setSubmitting, resetForm, setErrors }: FormikHelpers<TenantFormValues>,
  ) => {
    const data = {
      ...values,
      backOfficeEmail: (values.backOfficeEmail as string[]).join(','),
      nationalCode: values.nationalCode?.trim().replace(/[^A-Za-z0-9]/g, ''),
    }

    try {
      await axios.put(tenantDetailUrl, data)
      notification.success({ message: messages.common.success, description: messages.tenant.update.success })
      resetForm()
      onSuccess()
    } catch (error) {
      const formErrors = parseApiErrorResponse(error)
      setErrors(formErrors)

      notification.error({ message: messages.common.error, description: messages.tenant.update.error })
      return
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (tenant) {
      const { id, _links, ...formValues } = tenant
      formValues.backOfficeEmail = formValues?.backOfficeEmail
        ? formValues?.backOfficeEmail?.split(',').map((email: string) => email.trim())
        : []
      setInitialValues(formValues)
    }
  }, [tenant])

  if (error) {
    return <Result status="error" title={messages.tenant.retrieve.error} />
  }

  if (!issuers) {
    return <Skeleton />
  }

  return (
    <Row>
      <Col xs={24}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ handleBlur, setFieldValue, values }) => (
            <Form name="create-or-update-tenant">
              <FormItem name="name" label="Name" required>
                <Input name="name" placeholder="Tenant name" />
              </FormItem>
              <FormItem name="domain" htmlFor="domain" label="Domain" required>
                <Input
                  name="domain"
                  placeholder="Tenant domain"
                  prefix="https://"
                  suffix=".imperium.markets"
                  disabled
                />
              </FormItem>
              <FormItem name="isDemo" label="Is Demo">
                <Switch checkedChildren="Yes" unCheckedChildren="No" name="isDemo" />
              </FormItem>
              <FormItem name="isTrial" label="Is Trial">
                <Switch checkedChildren="Yes" unCheckedChildren="No" name="isTrial" />
              </FormItem>
              <FormItem name="timezone" label="Timezone" required>
                <Select
                  name="timezone"
                  placeholder="Select the timezone"
                  showSearch
                  options={localeNames.map((timezone: string) => ({ label: timezone, value: timezone }))}
                  {...filterProps}
                />
              </FormItem>
              <FormItem name="issuerId" htmlFor="issuerId" label="Issuer">
                <Select
                  data-testid="issuerId"
                  name="issuerId"
                  placeholder="Issuer id"
                  loading={!issuers}
                  showSearch
                  options={issuers?.list?.map(({ id, name }) => ({ label: `${id} - ${name}`, value: id }))}
                  {...filterProps}
                  disabled
                />
              </FormItem>
              <FormItem name="currency" label="Currency" required>
                <Select
                  name="currency"
                  placeholder="Select the currency"
                  showSearch
                  options={currencies.map((currency: string) => ({ label: currency, value: currency }))}
                  {...filterProps}
                />
              </FormItem>
              <FormItem name="backOfficeEmail" label="Back Office Email">
                <Select name="backOfficeEmail" placeholder="Back Office Email" mode="tags" allowClear />
              </FormItem>
              <FormItem name="defaultTPlusNDays" label="T+N">
                <InputNumber
                  name="defaultTPlusNDays"
                  placeholder="Default T+N"
                  min={0}
                  parser={(value) => value || 0}
                />
              </FormItem>
              <FormItem name="basisPointFee" label="Basis Point Fee">
                <InputNumber name="basisPointFee" placeholder="0" min={0} max={100} parser={(value) => value || 0} />
              </FormItem>
              <FormItem label="Has Separation of Duties" name="groupDuties">
                <div className="switch-input-dependent">
                  <FormItem name="enforceSeparationOfDuties">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" name="enforceSeparationOfDuties" />
                  </FormItem>
                  <FormItem name="requiredRfqSignOffCount" label="Signoff Count" className="sign-off-count">
                    <InputNumber
                      name="requiredRfqSignOffCount"
                      placeholder="Signoff Count"
                      disabled={!values?.enforceSeparationOfDuties}
                      min={0}
                      max={10}
                      parser={(value) => value || 0}
                      value={values?.enforceSeparationOfDuties ? values?.requiredRfqSignOffCount : 0}
                    />
                  </FormItem>
                </div>
              </FormItem>
              <FormItem name="segmentCode" label="Segment Code" required>
                <EditableSelect
                  addItemLabel="Add segment code"
                  addItemPlaceholder="Segment code"
                  placeholder="Select a segment code"
                  name="segmentCode"
                  showSearch
                  options={segmentCodes.map((currency: string) => ({ label: currency, value: currency }))}
                  {...filterProps}
                  onOptionAdded={(value: string) => setFieldValue('segmentCode', value)}
                />
              </FormItem>
              <FormItem name="passwordExpiryMonths" label="Password Expiry">
                <InputNumber
                  name="passwordExpiryMonths"
                  placeholder="Password Expiry in Months"
                  min={1}
                  max={12}
                  formatter={(value) => `${value} Months`}
                />
              </FormItem>
              <FormItem name="capitalBasisEnabled" label="Has Capital Basis">
                <Switch
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  name="capitalBasisEnabled"
                  disabled
                  data-testid="capitalBasis"
                />
              </FormItem>
              <FormItem name="longTermComplianceOnly" label="Has Long Term Compliance">
                <Switch checkedChildren="Yes" unCheckedChildren="No" name="longTermComplianceOnly" />
              </FormItem>
              <FormItem name="nationalCode" label="National Code">
                <Input
                  name="nationalCode"
                  placeholder="National Code (ABN)"
                  onBlur={(e) => {
                    const transformedValue = e.target.value.replace(/\D+/g, '')
                    setFieldValue('nationalCode', transformedValue)
                    handleBlur(e)
                  }}
                />
              </FormItem>
              <Column>
                <SubmitButton htmlType="submit" type="primary">
                  Update Tenant
                </SubmitButton>
              </Column>
            </Form>
          )}
        </Formik>
      </Col>
    </Row>
  )
}
